/* ==========================================================================
   PRODUCT SHOWCASE — prdct-product-showcase.js
   Vanilla JS. No dependencies. Class-based, one instance per section.
   ========================================================================== */

(function () {
  "use strict";

  var SELECTOR_ROOT = ".prdct-section";

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  function formatMoney(cents, format) {
    if (typeof cents !== "number") {
      cents = parseInt(cents, 10) || 0;
    }
    var value = (cents / 100).toFixed(2);
    var parts = value.split(".");
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var amount = withCommas + "." + parts[1];
    if (format) {
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return "$" + amount;
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /* ------------------------------------------------------------------ */
  /* Sticky Nav Controller                                               */
  /* ------------------------------------------------------------------ */

  function PrdctNavController(root) {
    this.root = root;
    this.nav = qs(root, "[data-prdct-nav]");
    if (!this.nav) return;
    this.scroller = qs(this.nav, "[data-prdct-nav-scroller]");
    this.list = qs(this.nav, "[data-prdct-nav-list]");
    this.indicator = qs(this.nav, "[data-prdct-nav-indicator]");
    this.links = qsa(this.nav, "[data-prdct-nav-link]");
    this.targets = this.links
      .map(function (link) {
        var id = link.getAttribute("href").replace("#", "");
        return document.getElementById(id);
      })
      .filter(Boolean);

    this.onLinkClick = this.onLinkClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.bind();
    this.observe();
    this.setActive(this.links[0], false);
    window.addEventListener("resize", this.onResize);
  }

  PrdctNavController.prototype.bind = function () {
    var self = this;
    this.links.forEach(function (link) {
      link.addEventListener("click", self.onLinkClick);
    });
    window.addEventListener("scroll", this.onScroll, { passive: true });
  };

  PrdctNavController.prototype.onScroll = function () {
    if (window.scrollY > 10) {
      this.nav.classList.add("is-stuck");
    } else {
      this.nav.classList.remove("is-stuck");
    }
  };

  PrdctNavController.prototype.onLinkClick = function (evt) {
    evt.preventDefault();
    var link = evt.currentTarget;
    var id = link.getAttribute("href").replace("#", "");
    var target = document.getElementById(id);
    if (!target) return;
    var navHeight = this.nav.getBoundingClientRect().height;
    var top =
      target.getBoundingClientRect().top + window.pageYOffset - navHeight - 16;
    window.scrollTo({ top: top, behavior: "smooth" });
    this.setActive(link, true);
  };

  PrdctNavController.prototype.setActive = function (link, scrollIntoNav) {
    if (!link) return;
    this.links.forEach(function (l) {
      l.parentElement.classList.remove("is-active");
      l.setAttribute("aria-current", "false");
    });
    link.parentElement.classList.add("is-active");
    link.setAttribute("aria-current", "true");
    this.moveIndicator(link);
    if (scrollIntoNav && this.scroller) {
      var itemRect = link.parentElement.getBoundingClientRect();
      var scrollerRect = this.scroller.getBoundingClientRect();
      if (itemRect.left < scrollerRect.left || itemRect.right > scrollerRect.right) {
        this.scroller.scrollTo({
          left:
            link.parentElement.offsetLeft -
            this.scroller.clientWidth / 2 +
            itemRect.width / 2,
          behavior: "smooth",
        });
      }
    }
  };

  PrdctNavController.prototype.moveIndicator = function (link) {
    if (!this.indicator) return;
    var item = link.parentElement;
    this.indicator.style.width = item.offsetWidth - 8 + "px";
    this.indicator.style.transform = "translateX(" + (item.offsetLeft + 4) + "px)";
  };

  PrdctNavController.prototype.onResize = function () {
    var active = qs(this.nav, ".prdct-nav__item.is-active .prdct-nav__link");
    if (active) this.moveIndicator(active);
  };

  PrdctNavController.prototype.observe = function () {
    var self = this;
    if (!("IntersectionObserver" in window) || this.targets.length === 0) return;
    this.io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var matchingLink = self.links.filter(function (l) {
              return l.getAttribute("href") === "#" + entry.target.id;
            })[0];
            if (matchingLink) self.setActive(matchingLink, false);
          }
        });
      },
      {
        rootMargin: "-" + (this.nav.offsetHeight + 20) + "px 0px -60% 0px",
        threshold: 0.01,
      }
    );
    this.targets.forEach(function (t) {
      self.io.observe(t);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Gallery Controller                                                  */
  /* ------------------------------------------------------------------ */

  function PrdctGalleryController(root) {
    this.root = root;
    this.gallery = qs(root, "[data-prdct-gallery]");
    if (!this.gallery) return;
    this.frames = qsa(this.gallery, "[data-prdct-frame]");
    this.thumbs = qsa(this.gallery, "[data-prdct-thumb]");
    this.prevBtn = qs(this.gallery, "[data-prdct-gallery-prev]");
    this.nextBtn = qs(this.gallery, "[data-prdct-gallery-next]");
    this.main = qs(this.gallery, "[data-prdct-gallery-main]");
    this.activeIndex = 0;

    this.bind();
  }

  PrdctGalleryController.prototype.bind = function () {
    var self = this;
    this.thumbs.forEach(function (thumb, index) {
      thumb.addEventListener("click", function () {
        self.goTo(index);
      });
      thumb.addEventListener("keydown", function (evt) {
        if (evt.key === "Enter" || evt.key === " ") {
          evt.preventDefault();
          self.goTo(index);
        }
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", function () {
        self.goTo(self.activeIndex - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", function () {
        self.goTo(self.activeIndex + 1);
      });
    }

    if (this.main) {
      this.main.setAttribute("tabindex", "0");
      this.main.addEventListener("keydown", function (evt) {
        if (evt.key === "ArrowLeft") self.goTo(self.activeIndex - 1);
        if (evt.key === "ArrowRight") self.goTo(self.activeIndex + 1);
      });

      // Touch swipe support
      var touchStartX = 0;
      var touchEndX = 0;
      this.main.addEventListener(
        "touchstart",
        function (evt) {
          touchStartX = evt.changedTouches[0].screenX;
        },
        { passive: true }
      );
      this.main.addEventListener(
        "touchend",
        function (evt) {
          touchEndX = evt.changedTouches[0].screenX;
          var delta = touchEndX - touchStartX;
          if (Math.abs(delta) > 40) {
            if (delta < 0) self.goTo(self.activeIndex + 1);
            else self.goTo(self.activeIndex - 1);
          }
        },
        { passive: true }
      );
    }
  };

  PrdctGalleryController.prototype.goTo = function (index) {
    var count = this.frames.length;
    if (count === 0) return;
    var next = ((index % count) + count) % count;
    this.activeIndex = next;

    this.frames.forEach(function (frame, i) {
      frame.classList.toggle("is-active", i === next);
    });
    this.thumbs.forEach(function (thumb, i) {
      thumb.classList.toggle("is-active", i === next);
      thumb.setAttribute("aria-selected", i === next ? "true" : "false");
    });

    var activeThumb = this.thumbs[next];
    if (activeThumb && typeof activeThumb.scrollIntoView === "function") {
      activeThumb.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  };

  PrdctGalleryController.prototype.setVariantImage = function (mediaId) {
    if (!mediaId) return;
    var index = this.frames.findIndex(function (frame) {
      return frame.getAttribute("data-prdct-media-id") === String(mediaId);
    });
    if (index > -1) this.goTo(index);
  };

  /* ------------------------------------------------------------------ */
  /* Accordion Controller                                                */
  /* ------------------------------------------------------------------ */

  function PrdctAccordionController(root) {
    this.items = qsa(root, "[data-prdct-accordion-item]");
    this.bind();
  }

  PrdctAccordionController.prototype.bind = function () {
    this.items.forEach(function (item) {
      var trigger = qs(item, "[data-prdct-accordion-trigger]");
      var panel = qs(item, "[data-prdct-accordion-panel]");
      if (!trigger || !panel) return;
      trigger.addEventListener("click", function () {
        var isOpen = item.classList.contains("is-open");
        item.classList.toggle("is-open", !isOpen);
        trigger.setAttribute("aria-expanded", !isOpen ? "true" : "false");
        if (!isOpen) {
          panel.style.maxHeight = panel.scrollHeight + "px";
        } else {
          panel.style.maxHeight = "0px";
        }
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Purchase Controller (variants, options, quantity, add to cart)      */
  /* ------------------------------------------------------------------ */

  function PrdctPurchaseController(root, productData, gallery) {
    this.root = root;
    this.product = productData;
    this.gallery = gallery;
    this.form = qs(root, "[data-prdct-form]");
    if (!this.form || !this.product) return;

    this.variantInput = qs(this.form, "[data-prdct-variant-id]");
    this.sellingPlanInput = qs(this.form, "[data-prdct-selling-plan-id]");
    this.quantityInput = qs(root, "[data-prdct-qty-input]");
    this.optionSwatches = qsa(root, "[data-prdct-option-value]");
    this.purchaseOptionEls = qsa(root, "[data-prdct-purchase-option]");
    this.submitBtn = qs(this.form, "[data-prdct-submit]");
    this.buyNowBtn = qs(root, "[data-prdct-buy-now]");
    this.errorBox = qs(root, "[data-prdct-form-error]");
    this.toast = qs(document, "[data-prdct-toast]");

    this.priceCurrentEl = qs(root, "[data-prdct-price-current]");
    this.priceCompareEl = qs(root, "[data-prdct-price-compare]");
    this.priceDiscountEl = qs(root, "[data-prdct-price-discount]");
    this.priceUnitEl = qs(root, "[data-prdct-price-unit]");
    this.availabilityEl = qs(root, "[data-prdct-availability]");

    this.selectedOptions = this.product.selected_or_first_available_variant
      ? this.product.selected_or_first_available_variant.options.slice()
      : this.product.variants[0].options.slice();

    this.purchaseType = "one_time";

    this.bind();
    this.updateVariant(this.getVariantFromOptions(), false);
  }

  PrdctPurchaseController.prototype.bind = function () {
    var self = this;

    this.optionSwatches.forEach(function (swatch) {
      swatch.addEventListener("click", function () {
        if (swatch.classList.contains("is-disabled")) return;
        var position = parseInt(swatch.getAttribute("data-prdct-option-position"), 10);
        var value = swatch.getAttribute("data-prdct-option-value");
        self.selectedOptions[position - 1] = value;

        qsa(self.root, '[data-prdct-option-position="' + position + '"]').forEach(
          function (s) {
            s.classList.remove("is-selected");
            s.setAttribute("aria-pressed", "false");
          }
        );
        swatch.classList.add("is-selected");
        swatch.setAttribute("aria-pressed", "true");

        var variant = self.getVariantFromOptions();
        self.updateVariant(variant, true);
      });
    });

    this.purchaseOptionEls.forEach(function (optionEl) {
      optionEl.addEventListener("click", function () {
        self.setPurchaseType(optionEl.getAttribute("data-prdct-purchase-option"));
      });
      optionEl.addEventListener("keydown", function (evt) {
        if (evt.key === "Enter" || evt.key === " ") {
          evt.preventDefault();
          self.setPurchaseType(optionEl.getAttribute("data-prdct-purchase-option"));
        }
      });
    });

    var qtyMinus = qs(this.root, "[data-prdct-qty-minus]");
    var qtyPlus = qs(this.root, "[data-prdct-qty-plus]");
    if (qtyMinus) {
      qtyMinus.addEventListener("click", function () {
        self.stepQuantity(-1);
      });
    }
    if (qtyPlus) {
      qtyPlus.addEventListener("click", function () {
        self.stepQuantity(1);
      });
    }
    if (this.quantityInput) {
      this.quantityInput.addEventListener("change", function () {
        var val = parseInt(self.quantityInput.value, 10);
        if (isNaN(val) || val < 1) val = 1;
        self.quantityInput.value = val;
      });
    }

    if (this.form) {
      this.form.addEventListener("submit", function (evt) {
        evt.preventDefault();
        self.addToCart(false);
      });
    }

    if (this.buyNowBtn) {
      this.buyNowBtn.addEventListener("click", function () {
        self.addToCart(true);
      });
    }
  };

  PrdctPurchaseController.prototype.stepQuantity = function (delta) {
    if (!this.quantityInput) return;
    var val = parseInt(this.quantityInput.value, 10) || 1;
    val = Math.max(1, val + delta);
    this.quantityInput.value = val;
  };

  PrdctPurchaseController.prototype.setPurchaseType = function (type) {
    if (!type) return;
    this.purchaseType = type;
    var self = this;
    this.purchaseOptionEls.forEach(function (el) {
      var isSelected = el.getAttribute("data-prdct-purchase-option") === type;
      el.classList.toggle("is-selected", isSelected);
      el.setAttribute("aria-checked", isSelected ? "true" : "false");
    });

    if (this.sellingPlanInput) {
      if (type === "subscription") {
        var firstPlanId = this.sellingPlanInput.getAttribute(
          "data-prdct-default-plan-id"
        );
        this.sellingPlanInput.value = firstPlanId || "";
        this.sellingPlanInput.disabled = false;
      } else {
        this.sellingPlanInput.value = "";
        this.sellingPlanInput.disabled = true;
      }
    }
    this.refreshPriceDisplay();
  };

  PrdctPurchaseController.prototype.getVariantFromOptions = function () {
    var self = this;
    return this.product.variants.filter(function (v) {
      return v.options.every(function (opt, i) {
        return opt === self.selectedOptions[i];
      });
    })[0];
  };

  PrdctPurchaseController.prototype.updateVariant = function (variant, userInitiated) {
    if (!variant) return;
    this.currentVariant = variant;
    if (this.variantInput) this.variantInput.value = variant.id;

    // disable/enable option combos with no matching variant
    var self = this;
    this.optionSwatches.forEach(function (swatch) {
      var position = parseInt(swatch.getAttribute("data-prdct-option-position"), 10);
      var value = swatch.getAttribute("data-prdct-option-value");
      var testOptions = self.selectedOptions.slice();
      testOptions[position - 1] = value;
      var matches = self.product.variants.some(function (v) {
        return v.options.every(function (opt, i) {
          return opt === testOptions[i];
        });
      });
      swatch.classList.toggle("is-disabled", !matches);
    });

    if (userInitiated && this.gallery && variant.featured_media) {
      this.gallery.setVariantImage(variant.featured_media.id);
    }

    this.refreshPriceDisplay();

    if (this.submitBtn) {
      this.submitBtn.disabled = !variant.available;
    }
    if (this.availabilityEl) {
      this.availabilityEl.textContent = variant.available ? "In stock" : "Sold out";
      this.availabilityEl.classList.toggle("is-out", !variant.available);
    }
  };

  PrdctPurchaseController.prototype.refreshPriceDisplay = function () {
    if (!this.currentVariant) return;
    var variant = this.currentVariant;
    var price = variant.price;
    var compareAt = variant.compare_at_price;

    if (this.purchaseType === "subscription" && variant.prdctSubscriptionPrice) {
      price = variant.prdctSubscriptionPrice;
    }

    if (this.priceCurrentEl) this.priceCurrentEl.textContent = formatMoney(price);
    if (this.priceCompareEl) {
      if (compareAt && compareAt > price) {
        this.priceCompareEl.textContent = formatMoney(compareAt);
        this.priceCompareEl.style.display = "";
        if (this.priceDiscountEl) {
          var pct = Math.round(((compareAt - price) / compareAt) * 100);
          this.priceDiscountEl.textContent = "-" + pct + "%";
          this.priceDiscountEl.style.display = "";
        }
      } else {
        this.priceCompareEl.style.display = "none";
        if (this.priceDiscountEl) this.priceDiscountEl.style.display = "none";
      }
    }
    if (this.priceUnitEl && variant.prdctServings) {
      var perServing = price / variant.prdctServings;
      this.priceUnitEl.textContent =
        formatMoney(perServing) + " per serving";
    }
  };

  PrdctPurchaseController.prototype.showError = function (message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.classList.add("is-visible");
    window.setTimeout(
      function (box) {
        box.classList.remove("is-visible");
      },
      5000,
      this.errorBox
    );
  };

  PrdctPurchaseController.prototype.showToast = function (message) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.classList.add("is-visible");
    window.setTimeout(
      function (toast) {
        toast.classList.remove("is-visible");
      },
      2600,
      this.toast
    );
  };

  PrdctPurchaseController.prototype.addToCart = function (redirectToCheckout) {
    var self = this;
    if (!this.currentVariant || !this.currentVariant.available) {
      this.showError("This combination is currently unavailable.");
      return;
    }

    var btn = redirectToCheckout ? this.buyNowBtn : this.submitBtn;
    if (btn) btn.classList.add("is-loading");

    var body = {
      items: [
        {
          id: this.currentVariant.id,
          quantity: this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1,
        },
      ],
    };

    if (
      this.purchaseType === "subscription" &&
      this.sellingPlanInput &&
      this.sellingPlanInput.value
    ) {
      body.items[0].selling_plan = this.sellingPlanInput.value;
    }

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + "cart/add.js"
      : "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent("prdct:cart:updated"));
        if (redirectToCheckout) {
          window.location.href = window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root + "checkout"
            : "/checkout";
          return;
        }
        self.showToast("Added to your cart");
      })
      .catch(function (err) {
        var message =
          (err && err.description) || "Something went wrong adding this to your cart.";
        self.showError(message);
      })
      .finally(function () {
        if (btn) btn.classList.remove("is-loading");
      });
  };

  /* ------------------------------------------------------------------ */
  /* Frequently Bought Together Controller                               */
  /* ------------------------------------------------------------------ */

  function PrdctFbtController(root) {
    this.root = root;
    this.wrapper = qs(root, "[data-prdct-fbt]");
    if (!this.wrapper) return;
    this.items = qsa(this.wrapper, "[data-prdct-fbt-item]");
    this.totalEl = qs(this.wrapper, "[data-prdct-fbt-total]");
    this.addAllBtn = qs(this.wrapper, "[data-prdct-fbt-add-all]");
    this.toast = qs(document, "[data-prdct-toast]");

    this.bind();
    this.recalculate();
  }

  PrdctFbtController.prototype.bind = function () {
    var self = this;
    this.items.forEach(function (item) {
      var checkbox = qs(item, "[data-prdct-fbt-checkbox]");
      if (!checkbox) return;
      checkbox.addEventListener("change", function () {
        item.classList.toggle("is-selected", checkbox.checked);
        self.recalculate();
      });
    });

    if (this.addAllBtn) {
      this.addAllBtn.addEventListener("click", function () {
        self.addSelected();
      });
    }
  };

  PrdctFbtController.prototype.getSelectedItems = function () {
    return this.items.filter(function (item) {
      var checkbox = qs(item, "[data-prdct-fbt-checkbox]");
      return checkbox && checkbox.checked;
    });
  };

  PrdctFbtController.prototype.recalculate = function () {
    var selected = this.getSelectedItems();
    var total = selected.reduce(function (sum, item) {
      var price = parseInt(item.getAttribute("data-prdct-fbt-price"), 10) || 0;
      return sum + price;
    }, 0);
    if (this.totalEl) this.totalEl.textContent = formatMoney(total);
    if (this.addAllBtn) this.addAllBtn.disabled = selected.length === 0;
  };

  PrdctFbtController.prototype.addSelected = function () {
    var self = this;
    var selected = this.getSelectedItems();
    if (selected.length === 0) return;

    this.addAllBtn.classList.add("is-loading");

    var items = selected.map(function (item) {
      return {
        id: parseInt(item.getAttribute("data-prdct-fbt-variant-id"), 10),
        quantity: 1,
      };
    });

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent("prdct:cart:updated"));
        if (self.toast) {
          self.toast.textContent = "Added " + selected.length + " items to your cart";
          self.toast.classList.add("is-visible");
          window.setTimeout(function () {
            self.toast.classList.remove("is-visible");
          }, 2600);
        }
      })
      .catch(function () {
        if (self.toast) {
          self.toast.textContent = "Couldn't add those items. Please try again.";
          self.toast.classList.add("is-visible");
          window.setTimeout(function () {
            self.toast.classList.remove("is-visible");
          }, 2600);
        }
      })
      .finally(function () {
        self.addAllBtn.classList.remove("is-loading");
      });
  };

  /* ------------------------------------------------------------------ */
  /* Section bootstrap                                                   */
  /* ------------------------------------------------------------------ */

  function initSection(root) {
    if (root.prdctInitialized) return;
    root.prdctInitialized = true;

    var dataScript = qs(root, "[data-prdct-product-json]");
    var productData = null;
    if (dataScript) {
      try {
        productData = JSON.parse(dataScript.textContent);
      } catch (e) {
        productData = null;
      }
    }

    new PrdctNavController(root);
    var gallery = new PrdctGalleryController(root);
    new PrdctAccordionController(root);
    new PrdctPurchaseController(root, productData, gallery);
    new PrdctFbtController(root);
  }

  function initAll() {
    qsa(document, SELECTOR_ROOT).forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (evt) {
    var root = qs(evt.target, SELECTOR_ROOT);
    if (root) initSection(root);
  });
})();
